import {useFrame, useThree} from "@react-three/fiber";
import {useXRInputSourceState} from "@react-three/xr";
import {GetPercentFromXToY, Lerp, Range} from "js-vextensions";
import {untracked} from "mobx";
import {RunInAction} from "mobx-graphlink";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import * as THREE from "three";
import {GetSelectedEngineConfig} from "../../../../../Server/DB/engineConfigs.js";
import {EngineConfig_VR, ImageFrameFlawType} from "../../../../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import {store} from "../../../../../Store/index.js";
import {GetControllerSide_Final} from "../../../../../Store/main/settings/vr.js";
import {ImageFrame} from "../../../../../Store/main/tools/vr/ImageFrame.js";
import {Remote_MemoryPalaceNotifyRecallResult, Remote_NotifyUserChangedActiveFrame, Remote_RequestPlayAudioClipForFrame, Remote_RequestTTS, Remote_RunCLVoicing, Remote_StopTTSIfLastUtteranceIdIsX} from "../../../../../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {MemoryPalaceFrame, memoryPalaceFrameDB} from "../../../../../Utils/IndexedDB/MemoryPalaceFrameDB.js";
import {NewUtteranceID} from "../../../../../Utils/Services/TTS.js";
import {GetTinyTransparentImageTexture, tinyTransparentImageAsBase64Str, useBase64ImageStrAsTexture, useVideoElementAsTexture} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {getControllerPosAndDir} from "../../../../../Utils/ThreeJS/XR/Controller.js";
import {LHQInstance} from "../../LHQ/Scene.js";
import {raycaster_main} from "../../Utils/Raycasters.js";
import {vr_memoryPalacePromptLastRequestedAt} from "../SceneXREventListeners.js";
import {MemoryPalace_RecallResult} from "../../../../../Engine/LiveSession/SessionEvent.js";
import {ImageFrameUI} from "./ImageFrameUI.js";

const pixelsPerMeter = 200;
export const ImageFrames = observer_mgl_r3((props: {lhq: LHQInstance})=>{
	const {lhq} = props;
	const vrState = store.main.tools.vr;
	const framesSettings = store.main.settings.vr;
	const {gl} = useThree();
	const randomSeed = useMemo(()=>Math.random(), []);
	const config = GetSelectedEngineConfig()?.vr;

	const self = useMemo(()=>({
		prevActiveFrame: untracked(()=>config ? vrState.GetActiveImageFrame(config) : null) as ImageFrame|n,
	}), []);

	// grip down fully = increase vrState.imageFrameOffset by 1/s, trigger down fully, decreases by 1/s (speed scales with hold-amount, eg. held down only 50% = 0.5/s)
	useFrame((state, delta, xrFrame)=>{
		if (config == null) return;
		if (vrState.right_interactionMode != "frames") return;
		if (!gl.xr.isPresenting || gl.xr.getSession()?.inputSources == null) return;

		// if delta is >0.5s, assume the long pause was due to being paused in dev-tools or something; limit delta to 0.5s!
		// (makes debugging stuff easier, by not causing tons of image-frames to load in all at once after unpausing!)
		if (delta > 0.5) delta = 0.5;

		// apply image-frame flickering
		if (config.frames_flickerOnDuration > 0 && config.frames_flickerOffDuration > 0) {
			vrState.imageFrames_lastFlickerCheckTime = Date.now();

			const targetFlickerDuration = vrState.imageFrames_currentFlickerVisibility ? config.frames_flickerOnDuration : config.frames_flickerOffDuration;
			const timeSinceLastFlicker = Date.now() - vrState.imageFrames_lastFlickerTime;
			if (timeSinceLastFlicker >= targetFlickerDuration) {
				const timeTakenBeyondTarget = timeSinceLastFlicker - targetFlickerDuration;
				RunInAction("ImageFrames_flicker", ()=>{
					vrState.imageFrames_currentFlickerVisibility = !vrState.imageFrames_currentFlickerVisibility;
					const nextFlickerDuration = vrState.imageFrames_currentFlickerVisibility ? config.frames_flickerOnDuration : config.frames_flickerOffDuration;
					// if we were slow applying this flicker, schedule the next flicker to happen at the time it would have if we weren't late
					// BUT limit this leftward scheduling-shift to half the next flicker's duration (to avoid flickering every frame, if there's a very long pause in rendering fsr)
					vrState.imageFrames_lastFlickerTime = Date.now() - timeTakenBeyondTarget.KeepAtMost(nextFlickerDuration / 2);
				});
			}
		}

		const session = gl.xr.getSession()!;
		// NOTE: Not all controller input is handled here. (see also: SceneXREventListeners.tsx)
		for (const inputSource of session.inputSources) {
			const side = inputSource.handedness;
			if (side != GetControllerSide_Final("right")) continue;
			if (!inputSource.gamepad) continue;

			const [triggerIndex, gripIndex] = framesSettings.imageFrame_swapDirectionalControls ? [1, 0] : [0, 1];
			const triggerDownPercent = inputSource.gamepad.buttons[triggerIndex].value;
			const gripDownPercent = inputSource.gamepad.buttons[gripIndex].value;

			// offset motion, summary: if grip down, move forward; if trigger down, move backward; if both down, move forward at 2x speed
			// details:
			// * G (grip-value), T (trigger-value), TWG (portion of T <= G), TBG (portion of T > G)
			// * FV (forward-value) = G + TWG, BV (backward-value) = TBG
			// * V (velocity) = FV - BV
			const gripValue = gripDownPercent * 1; // (intuitive for 1s holding down to offset +/- 1)
			const triggerValue = triggerDownPercent * 1; // (intuitive for 1s holding down to offset +/- 1)
			const triggerValueWithinGripValue = Math.min(triggerValue, gripValue);
			const triggerValueBeyondGripValue = triggerValue - triggerValueWithinGripValue;
			const forwardValue = gripValue + triggerValueWithinGripValue;
			const backwardValue = triggerValueBeyondGripValue;
			const velocity = forwardValue - backwardValue;

			const offsetDelta_for1Sec = velocity;
			//const offsetDelta_forOneFrame = offsetDelta_for1Sec * (1 / (gl.xr.getSession()?.frameRate ?? 90));
			const offsetDelta_forOneFrame = offsetDelta_for1Sec * delta;
			RunInAction("ImageFrames_useFrame", ()=>{
				//const prevActiveFrame = vrState.GetActiveImageFrame(config);
				vrState.imageFrameOffset += offsetDelta_forOneFrame;
			});
		}

		// if frame-offset is 0 (ie. initial state), skip rest of processing (it's only relevant once user starts interacting with image-frames system, and it can cause significant network traffic)
		if (vrState.imageFrameOffset == 0) return;

		// start by ensuring there's at least one image-frame in the array (so later code can be sure that earliestFrame, latestFrame, etc. are always non-null)
		if (vrState.imageFrames.length == 0) {
			const newFrame = new ImageFrame({startOffset: 0});
			newFrame.AcquireFrameDataAndResolveMetadata(config, vrState.imageFrames.LastOrX());
			RunInAction("ImageFrames_useFrame", ()=>vrState.imageFrames.push(newFrame));
		}

		// note: for all of these "ranges", a frame is considered part of it if its own range *intersects* the given range
		const loadRange_start = vrState.imageFrameOffset - (1 * config.frames_duration);
		const loadRange_end = vrState.imageFrameOffset + (framesSettings.imageFrame_preloadCount * config.frames_duration);

		// if new offset is nearing the edge of the existing image-frames, generate new ones to fill the gap
		const earliestFrame = vrState.imageFrames[0];
		const latestFrame = vrState.imageFrames[vrState.imageFrames.length - 1];
		if (loadRange_start < earliestFrame.startOffset) {
			const newFrame = new ImageFrame({
				startOffset: earliestFrame.startOffset - config.frames_duration,
			});
			newFrame.AcquireFrameDataAndResolveMetadata(config, vrState.imageFrames[0]);
			RunInAction("ImageFrames_useFrame", ()=>vrState.imageFrames.unshift(newFrame));
		}
		if (loadRange_end >= latestFrame.EndOffset(config)) {
			const newFrame = new ImageFrame({
				startOffset: latestFrame.EndOffset(config),
			});
			newFrame.AcquireFrameDataAndResolveMetadata(config, vrState.imageFrames.LastOrX());
			RunInAction("ImageFrames_useFrame", ()=>vrState.imageFrames.push(newFrame));
		}

		const newActiveFrame = vrState.GetActiveImageFrame(config);
		if (newActiveFrame != self.prevActiveFrame && newActiveFrame) {
			// perform cleanup of "heavy" data for "now expired" frames
			const expireRange_lastFrameToExpire_endOffset = newActiveFrame.EndOffset(config) - (framesSettings.imageFrame_expireAfter * config.frames_duration);
			for (const frame of vrState.imageFrames) {
				// if frame is expired, and cleanup has not already been performed, do that cleanup (ie. clear its "heavy" data, like image/video data)
				if (frame.EndOffset(config) <= expireRange_lastFrameToExpire_endOffset && !frame.cleanupDone) {
					frame.Cleanup();
				}
			}

			// if memory-palace was used during showing of previously-active frame, record a MemoryPalace.RecallResult event
			if (self.prevActiveFrame?.currentMemoryPromptInfo) {
				const promptInfo = self.prevActiveFrame.currentMemoryPromptInfo;
				if (promptInfo) {
					const timeOfEncodeNextDone = Date.now();
					if (promptInfo.time_doneWithDecodeAttempt == null) {
						RunInAction("ImageFrames_useFrame", ()=>promptInfo.time_doneWithNextEncode = timeOfEncodeNextDone);
					}
					if (promptInfo.actionType == "find") {
						(async()=>{
							const targetEncodes = await memoryPalaceFrameDB.GetFramesForTarget(promptInfo.target);
							// the most recent encode is ignored (ie. the one that "just happened" for currently-shown prompt), so must have 2 (ie. one prior)
							if (targetEncodes.length >= 2) {
								const lastPriorEncode = targetEncodes.OrderByDescending(a=>a.createdAt)[1];
								const encodeToDecodeDelay = promptInfo.time_decodeStart - lastPriorEncode.createdAt;
								const timeOfEncodeNextStart = promptInfo.time_decodeMarkedAsFailed ?? promptInfo.time_doneWithDecodeAttempt;
								const data: MemoryPalace_RecallResult = {
									target: promptInfo.target,
									decodeSuccess: promptInfo.time_decodeMarkedAsFailed == null,
									encodeToDecodeDelay,
									decodeAttemptDuration: promptInfo.time_doneWithDecodeAttempt ? (promptInfo.time_doneWithDecodeAttempt - promptInfo.time_decodeStart) : undefined,
									encodeNextDuration: timeOfEncodeNextStart ? (timeOfEncodeNextDone - timeOfEncodeNextStart) : undefined,
								};
								await Remote_MemoryPalaceNotifyRecallResult(data);
							}
						})();
					}
				}
			}

			// notify remote session, if first time showing new-frame (eg. for session event-recording, and daily-goal progress-tracking)
			if (!newActiveFrame.toldRemoteOfDisplay) {
				Remote_NotifyUserChangedActiveFrame(!newActiveFrame.toldRemoteOfDisplay).then(({newCount, newTimeCredit, newMPPCount})=>{
					RunInAction("ImageFrames_useFrame", ()=>{
						vrState.imageFramesDisplayed_duringRemoteSession = newCount;
						vrState.timeCredit_duringRemoteSession = newTimeCredit;
						vrState.memoryPalacePrompts_duringRemoteSession = newMPPCount;
					});
				});
				newActiveFrame.toldRemoteOfDisplay = true;
			}

			// reset snapshot-reveal-level whenever active-frame changes
			if (vrState.snapshotRevealLevel != 0) {
				RunInAction("ImageFrames_useFrame", ()=>vrState.snapshotRevealLevel = 0);
			}
		}

		self.prevActiveFrame = newActiveFrame;
	});

	if (config == null) return null;
	if (vrState.right_interactionMode != "frames") return null;
	const activeFrameIndex = vrState.imageFrames.indexOf(vrState.GetActiveImageFrame(config) as any);
	return vrState.imageFrames.map((imageFrame, index)=>{
		// special case: if this is the frame after the active one, and a "jump to next image-frame" was requested, then also render this next frame
		const percentThroughJump = vrState.GetPercentThroughJump();
		if (percentThroughJump != null && activeFrameIndex != -1 && index == activeFrameIndex + 1) {
			return <ImageFrameUI key={imageFrame.id} {...{lhq, config}} frame={imageFrame}/>;
		}

		if (vrState.imageFrameOffset < imageFrame.startOffset || vrState.imageFrameOffset >= imageFrame.EndOffset(config)) return null;
		return <ImageFrameUI key={imageFrame.id} {...{lhq, config}} frame={imageFrame}/>;
	});
});